import type {
  CoachingSession,
  ParticipationReport,
} from '@efdigital/ll-addon-data';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import { participationReportLabel } from '@efdigital/ll-xceed-v2/utils/coaching/participation-report-display';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface ConfirmAttendanceModalSignature {
  Args: {
    session: CoachingSession;
    planner: CoachingPlanner;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

export class ConfirmAttendanceModal extends Component<ConfirmAttendanceModalSignature> {
  @tracked open = false;
  @tracked loading = false;
  @tracked report: ParticipationReport = 'session_delivered';
  @tracked issueDetails = '';

  closeModal = () => {
    this.open = false;
  };

  get confirmButtonDisabled(): boolean {
    if (this.loading) {
      return true;
    }

    if (this.isNeedsReview) {
      return this.issueDetails.trim().length === 0;
    }

    return false;
  }

  submitReport = async () => {
    this.loading = true;
    try {
      await this.args.planner.markAttendance(this.args.session, {
        report: this.report,
        ...(this.isNeedsReview
          ? { issueDetails: this.issueDetails.trim() }
          : {}),
      });
      this.open = false;
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  };

  showModal = () => {
    this.report = 'session_delivered';
    this.issueDetails = '';
    this.open = true;
  };

  setSessionDelivered = () => {
    this.report = 'session_delivered';
  };

  setIDidNotAttend = () => {
    this.report = 'i_did_not_attend';
  };

  setOtherDidNotAttend = () => {
    this.report = 'other_did_not_attend';
  };

  setNeedsReview = () => {
    this.report = 'reported_issue';
  };

  updateIssueDetails = (event: Event) => {
    this.issueDetails = (event.target as HTMLTextAreaElement).value;
  };

  get isSessionDelivered(): boolean {
    return this.report === 'session_delivered';
  }

  get isIDidNotAttend(): boolean {
    return this.report === 'i_did_not_attend';
  }

  get isOtherDidNotAttend(): boolean {
    return this.report === 'other_did_not_attend';
  }

  get isNeedsReview(): boolean {
    return this.report === 'reported_issue';
  }

  <template>
    {{#if this.open}}
      <BsModal @open={{this.open}} as |modal|>
        <modal.header>
          <h5 class="modal-title">Report session outcome</h5>
        </modal.header>
        <modal.body>
          <p>What happened in this session?</p>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="participation-report"
              id="coachee-report-delivered"
              checked={{this.isSessionDelivered}}
              {{on "change" this.setSessionDelivered}}
            />
            <label class="form-check-label" for="coachee-report-delivered">
              {{participationReportLabel "session_delivered" "coachee"}}
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="participation-report"
              id="coachee-report-self-absent"
              checked={{this.isIDidNotAttend}}
              {{on "change" this.setIDidNotAttend}}
            />
            <label class="form-check-label" for="coachee-report-self-absent">
              {{participationReportLabel "i_did_not_attend" "coachee"}}
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="participation-report"
              id="coachee-report-other-absent"
              checked={{this.isOtherDidNotAttend}}
              {{on "change" this.setOtherDidNotAttend}}
            />
            <label class="form-check-label" for="coachee-report-other-absent">
              {{participationReportLabel "other_did_not_attend" "coachee"}}
            </label>
          </div>
          <div class="form-check mb-3">
            <input
              class="form-check-input"
              type="radio"
              name="participation-report"
              id="coachee-report-needs-review"
              checked={{this.isNeedsReview}}
              {{on "change" this.setNeedsReview}}
            />
            <label class="form-check-label" for="coachee-report-needs-review">
              {{participationReportLabel "reported_issue" "coachee"}}
            </label>
          </div>
          {{#if this.isNeedsReview}}
            <div class="mb-3">
              <label class="form-label" for="coachee-issue-details">
                What happened?
              </label>
              <textarea
                id="coachee-issue-details"
                class="form-control"
                rows="4"
                value={{this.issueDetails}}
                placeholder="Describe what happened and any follow-up needed."
                {{on "input" this.updateIssueDetails}}
              ></textarea>
            </div>
            <p class="text-muted small mb-0">
              We will review your report and take appropriate action. You can
              also email
              <a
                href="mailto:v-support@hultashridge.com"
              >v-support@hultashridge.com</a>
              if you need immediate help.
            </p>
          {{/if}}
        </modal.body>
        <modal.footer>
          <button
            class="btn btn-outline-secondary"
            type="button"
            {{on "click" this.closeModal}}
            disabled={{this.loading}}
          >Cancel</button>
          <button
            class="btn btn-primary"
            type="button"
            {{on "click" this.submitReport}}
            disabled={{this.confirmButtonDisabled}}
          >Submit report</button>
        </modal.footer>
      </BsModal>
    {{/if}}
    {{yield this.showModal}}
  </template>
}
